import * as CL from '@design-system/component-library';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SELECTED_COMMERCIAL_PRODUCT_INDEX } from '../../common/constants/commonConstants.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { SelectedPurposeOfUseOrContact } from '../../common/enums.js';
import { activationFeeMsg, nextMsg, sumPerMonthMsg, t } from '../../common/i18n/index.js';
import { getCommercialProductPrices } from '../../common/utils/commercialProductUtils.js';
import { handleUpsertContactResult } from '../../common/utils/contactUtils.js';
import { formatSum } from '../../common/utils/priceUtils.js';
import { CompanyInfoResponse, SalesType } from '../../generated/api/models.js';
import { useSubscriptionAction } from '../../public/site/SubscriptionActionProvider.js';
import { addNotification } from '../../public/site/notificationStore';
import { clearSubscriptionAction } from '../../public/site/subscriptionActionStore.js';
import { ContactOrPurposeOfUse } from '../ContactOrPurposeOfUse/ContactOrPurposeOfUse.js';
import { useContactsEdit } from '../ContactsEditContext/ContactsEditContext.js';
import { getNewContactFromPurposeOfUseOrContact } from './FixedBroadbandUtils.js';
import { NettiLiteProductInfoHeader } from './NettiLiteProductInfoHeader.js';
import type { YritystuplaOfferSelection } from './FixedBroadbandCheckout.js';
import type { ConfiguredOffer } from '../../common/types/commercialProduct.js';
import type { PurposeOfUseOrContact } from '../../common/types/subscription.js';
import type { AddOn, CampaignAssociation, Contact } from '../../generated/api/models.js';
import type { AddressSearchMatch } from '../AddressSearch/AddressSearch.js';
import type { ContactOrPurposeOfUseCallables } from '../ContactOrPurposeOfUse/ContactOrPurposeOfUse.js';

import './FixedBroadbandCheckout.scss';

interface FixedBroadbandProductProps {
  addressSearchMatch: AddressSearchMatch;
  onNext: (contact: PurposeOfUseOrContact, additionalAddOns: AddOn[], yritystuplaPoU?: PurposeOfUseOrContact) => void;
  configuredOffer: ConfiguredOffer;
  selectedCampaign?: CampaignAssociation;
  companyInfo?: CompanyInfoResponse;
  selectedPurposeOfUseOrContact?: PurposeOfUseOrContact;
  yritystuplaOffer: YritystuplaOfferSelection;
  contacts: Contact[];
}

export const FixedBroadbandProduct = (props: FixedBroadbandProductProps) => {
  const dispatch = useDispatch();
  const {
    addressSearchMatch,
    configuredOffer,
    onNext,
    companyInfo,
    selectedPurposeOfUseOrContact,
    selectedCampaign,
    yritystuplaOffer,
    contacts,
  } = props;
  const isInternalCustomer = companyInfo?.customerType === CompanyInfoResponse.CustomerTypeEnum.INTERNAL_CUSTOMERS;

  const { upsertContact: upsertContactEdit, saving } = useContactsEdit();
  const { useDuplicateContact: selectedDuplicateContact } = useSubscriptionAction();

  const contactOrPurposeOfUseRef = useRef<ContactOrPurposeOfUseCallables>(null);
  const yritystuplaPurposeOfUseRef = useRef<ContactOrPurposeOfUseCallables>(null);
  const [selectedContactId, setSelectedContactId] = useState<string>();

  // Clear any stale duplicate-contact state when (re)entering the checkout
  useEffect(() => {
    clearSubscriptionAction();
  }, []);

  const purposeOfUseOrContact = selectedPurposeOfUseOrContact || {
    selected: SelectedPurposeOfUseOrContact.CONTACT,
    contactId: selectedContactId,
  };

  const configuredCommercialProduct = configuredOffer.selectedCommercialProducts[SELECTED_COMMERCIAL_PRODUCT_INDEX];
  const selectedCommercialProduct = configuredCommercialProduct.commercialProduct;

  const onSubmit = async () => {
    const nettiLiteContactOrPurposeOfUse = await contactOrPurposeOfUseRef.current?.getValues(true);
    const yritystuplaPurposeOfUse = await yritystuplaPurposeOfUseRef.current?.getValues(true);
    const newContact = getNewContactFromPurposeOfUseOrContact(nettiLiteContactOrPurposeOfUse || undefined);

    const validYritystuplaPurpose = !yritystuplaOffer.enabled || Boolean(yritystuplaPurposeOfUse);

    if (selectedDuplicateContact && validYritystuplaPurpose && yritystuplaPurposeOfUse !== false) {
      onNext(selectedDuplicateContact, [], yritystuplaPurposeOfUse);
    } else if (newContact) {
      const result = await upsertContactEdit(newContact);
      handleUpsertContactResult(result, dispatch, {
        onFailure: () => addNotification(t.GXHR('Failed to create contact'), 'error'),
        onSuccess: successResult => onNext(successResult.contact, [], yritystuplaPurposeOfUse || undefined),
      });
    } else if (nettiLiteContactOrPurposeOfUse && validYritystuplaPurpose && yritystuplaPurposeOfUse !== false) {
      onNext(nettiLiteContactOrPurposeOfUse, [], yritystuplaPurposeOfUse);
    }
  };

  const broadbandPrice = getCommercialProductPrices(selectedCommercialProduct, selectedCampaign, SalesType.NEW_SALE);
  const broadbandPeriod = selectedCommercialProduct?.campaignAssociations?.[0]?.fixedTermPeriod || 0;
  const yritystuplaPrice = yritystuplaOffer.selectedOffer?.commercialProducts?.[0]?.price;

  return (
    <div className="of-fixed-bb-checkout">
      <NettiLiteProductInfoHeader
        productName={configuredOffer.offer.offerName}
        productInformation={`${addressSearchMatch.addressText}, ${addressSearchMatch.postalCode}`}
        price={t.YO7F(sumPerMonthMsg, formatSum(broadbandPrice.monthlyRecurringCharge) || '')}
        type="broadband"
      >
        <span className={`${dsClass.FONT_SIZE_SMALL} ${dsClass.DISPLAY_BLOCK}`}>
          {`${t.HWDR(activationFeeMsg)} ${formatSum(broadbandPrice.oneTimeCharge)}`}
        </span>
        {broadbandPeriod ? (
          <span className={`${dsClass.FONT_SIZE_SMALL} ${dsClass.DISPLAY_BLOCK}`}>
            {t.CF93('{} month period', broadbandPeriod.toString())}
          </span>
        ) : null}
      </NettiLiteProductInfoHeader>
      <div
        className={`of-fixed-bb-checkout__installation-info of-fixed-bb-checkout__installation-info--bordered ${dsClass.MARGIN_TOP_5}`}
      >
        <h3>{t.EAAJ('Subscription Details')}</h3>
        <ContactOrPurposeOfUse
          id="1"
          purposeOfUseOrContact={selectedDuplicateContact || purposeOfUseOrContact}
          contacts={contacts}
          ref={contactOrPurposeOfUseRef}
          required={true}
          isEmailAndPhoneRequired={true}
          enableCostCenterAndReference={true}
          isCostCenterRequired={isInternalCustomer}
          onChangeContact={(contactId?: string) => {
            setSelectedContactId(contactId);
            clearSubscriptionAction();
          }}
        />
      </div>
      {yritystuplaOffer.enabled && yritystuplaOffer.selectedOffer ? (
        <div className={dsClass.MARGIN_TOP_8}>
          <NettiLiteProductInfoHeader
            productName={yritystuplaOffer.selectedOffer.offerName}
            price={t.YO7F(sumPerMonthMsg, formatSum(yritystuplaPrice?.effectivePrice.monthlyRecurringCharge) || '')}
            type="yritystupla"
          >
            {yritystuplaPrice?.effectivePrice?.oneTimeCharge ? (
              <span
                className={`${dsClass.FONT_SIZE_SMALL} ${dsClass.DISPLAY_BLOCK}`}
              >{`${t.HWDR(activationFeeMsg)} ${formatSum(yritystuplaPrice.effectivePrice.oneTimeCharge)}`}</span>
            ) : null}
          </NettiLiteProductInfoHeader>
          <div
            className={`of-fixed-bb-checkout__installation-info of-fixed-bb-checkout__installation-info--bordered ${dsClass.MARGIN_TOP_5}`}
          >
            <h3>{t.EAAJ('Subscription Details')}</h3>
            <ContactOrPurposeOfUse
              id="yritystupla-purpose-of-use"
              purposeOfUseOrContact={{ selected: SelectedPurposeOfUseOrContact.PURPOSEOFUSE }}
              ref={yritystuplaPurposeOfUseRef}
              required={true}
              isEmailAndPhoneRequired={false}
              enableCostCenterAndReference={true}
              isCostCenterRequired={isInternalCustomer}
              onlyPurposeOfUse={true}
            />
          </div>
        </div>
      ) : null}

      <div className="ea-m-t-4" />
      <CL.Button className={`${dsClass.MARGIN_TOP_4} of-next-button`} loading={saving} onClick={onSubmit} size="l">
        {t.F0MY(nextMsg).toUpperCase()}
      </CL.Button>
    </div>
  );
};
